import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Send, Upload, CheckCircle } from 'lucide-react';

export const Contact = () => {
  const { language } = useLanguage();
  const location = useLocation();
  
  // Get type from URL query params
  const searchParams = new URLSearchParams(location.search);
  const contactType = searchParams.get('type'); // 'invest', 'work', or 'partner'
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    message: '',
    type: contactType || 'general',
    cvFile: null,
    linkedin: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Update type when URL changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, type: contactType || 'general' }));
    setIsSubmitted(false);
  }, [contactType]);

  // Dynamic content based on type - Updated messaging
  const getContent = () => {
    if (contactType === 'invest') {
      return {
        heading: language === 'fr'
          ? 'Investir avec nous'
          : 'Invest with us',
        subheading: language === 'fr'
          ? 'Ocean Peak Group s\'adresse aux investisseurs qui souhaitent soutenir une thèse industrielle exigeante, fondée sur la discipline d\'investisseur et le temps long. Nous investissons dans des entreprises industrielles positionnées sur des marchés de niche, avec des produits complexes à forte valeur ajoutée, reposant sur un savoir-faire industriel différenciant. Notre ambition : construire, dans la durée, des champions industriels à l\'échelle mondiale.'
          : 'Ocean Peak Group is built for investors who want to back a rigorous industrial thesis, one grounded in depth, discipline, and a long-term horizon. We invest in industrial businesses positioned in niche markets, with complex, high-value-added products built on genuinely differentiating industrial know-how. Our ambition: to build, over time, world-class industrial champions.'
      };
    } else if (contactType === 'work') {
      return {
        heading: language === 'fr'
          ? 'Travailler avec nous'
          : 'Join our team',
        subheading: language === 'fr'
          ? 'Rejoindre Ocean Peak Group, c\'est s\'engager au cœur de projets industriels concrets, au plus près des décisions et des réalités opérationnelles. Nous recherchons des profils qui partagent notre exigence, notre proximité terrain et notre engagement envers l\'industrie. Chez Ocean Peak Group, chaque mission est un terrain d\'apprentissage unique, où la rigueur se combine avec le pragmatisme et où l\'impact se mesure dans la durée.'
          : 'Joining Ocean Peak Group means working at the heart of real industrial projects, close to the decisions, close to the action. We look for people who share our high standards, our appetite for depth, and our commitment to European industry. At Ocean Peak Group, every assignment is a unique proving ground where rigour meets pragmatism, and impact is measured over the long run.'
      };
    } else if (contactType === 'partner') {
      return {
        heading: language === 'fr'
          ? 'Devenir partenaire'
          : 'Become a partner',
        subheading: language === 'fr'
          ? 'S\'associer à Ocean Peak Group, c\'est faire le choix d\'un partenaire capable d\'intervenir lorsque la complexité augmente, que les décisions deviennent structurantes et que les trajectoires doivent être sécurisées. Dans l\'industrie, la performance durable ne se décrète pas. Elle se construit sur des fondamentaux solides, une compréhension fine des réalités opérationnelles et une capacité à décider avec lucidité dans des contextes contraints. Notre intervention repose sur trois principes : comprendre avant d\'agir, stabiliser les fondamentaux, bâtir dans la durée.'
          : 'Partnering with Ocean Peak Group means choosing a partner who steps in when complexity rises, when decisions become critical, and when trajectories need to be secured. In industry, lasting performance isn\'t declared; it\'s built. On solid foundations, a clear-eyed understanding of operational realities, and the ability to make sharp decisions under real constraints.'
      };
    }
    return {
      heading: language === 'fr' ? 'Entrons en contact' : 'Let\'s Talk',
      subheading: language === 'fr'
        ? 'Dites-nous comment nous pouvons vous accompagner.'
        : 'Tell us how we can work together.'
    };
  };

  const content = getContent();

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'cvFile') {
      setFormData(prev => ({ ...prev, cvFile: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
            // Send form data to API
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          company: formData.company,
          message: formData.message,
          type: formData.type,
          linkedin: formData.linkedin
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      toast.success(language === 'fr' ? 'Message envoyé avec succès!' : 'Message sent successfully!');
      setIsSubmitted(true);
            setFormData({ 
        firstName: '', 
        lastName: '', 
        email: '', 
        company: '', 
        message: '', 
        type: contactType || 'general',
        cvFile: null,
        linkedin: ''
      });
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Error sending message');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-technical-white">
      {/* Hero Section with Background Image */}
      <section 
        className="relative h-[500px] flex flex-col overflow-hidden"
        style={{ backgroundColor: '#0a1f35' }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/hero-contact.jpg)'
          }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(10, 31, 53, 0.5)' }} />
        
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 text-center">
          {/* Heading */}
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl mb-6 text-white"
            style={{ 
              fontFamily: "'Bank Gothic', 'Impact', sans-serif", 
              fontWeight: 900,
              letterSpacing: '0.02em',
              textTransform: 'uppercase'
            }}
          >
            {content.heading}
          </h1>
          
          {/* Subheading */}
          <p 
            className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto"
            style={{ fontFamily: "'Eurostile', 'Arial', sans-serif" }}
          >
            {content.subheading}
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="bg-white p-8 md:p-12 shadow-lg">
            {/* Success Message */}
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-steel-grey/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-abyssal-blue" />
                  </div>
                </div>
                <h2 
                  className="text-2xl text-abyssal-blue mb-4"
                  style={{ fontFamily: "'Bank Gothic', 'Impact', sans-serif", textTransform: 'uppercase' }}
                >
                  {language === 'fr' ? 'Message Reçu' : 'Message Received'}
                </h2>
                <p className="text-steel-grey text-lg mb-6" style={{ fontFamily: "'Eurostile', 'Arial', sans-serif" }}>
                  {language === 'fr'
                    ? 'Merci. Votre message a bien été envoyé. Nous vous répondrons dans les meilleurs délais.'
                    : 'Thank you. Your message has been sent. We\'ll be in touch shortly.'}
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-abyssal-blue hover:text-steel-grey transition-colors font-medium underline"
                >
                  {language === 'fr' ? 'Envoyer un autre message' : 'Send another message'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Reason for Reaching Out - Dropdown at TOP */}
                <div className="space-y-2">
                  <Label htmlFor="type" className="text-abyssal-blue font-medium">
                    {language === 'fr' ? 'Objet de votre demande' : 'Subject'} *
                  </Label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 py-2 border border-steel-grey/30 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-light-blue focus:border-transparent"
                    style={{ fontFamily: "'Eurostile', 'Arial', sans-serif" }}
                  >
                    <option value="general">{language === 'fr' ? 'Demande générale' : 'General inquiry'}</option>
                    <option value="invest">{language === 'fr' ? 'Investir avec nous' : 'Invest with us'}</option>
                    <option value="work">{language === 'fr' ? 'Travailler avec nous' : 'Join our team'}</option>
                    <option value="partner">{language === 'fr' ? 'Devenir partenaire' : 'Become a partner'}</option>
                  </select>
                </div>

                {/* First and Last Name */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-abyssal-blue font-medium">
                      {language === 'fr' ? 'Prénom' : 'First Name'} *
                    </Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      placeholder={language === 'fr' ? 'Votre prénom' : 'Your first name'}
                      className="border-steel-grey/30"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-abyssal-blue font-medium">
                      {language === 'fr' ? 'Nom' : 'Last Name'} *
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      placeholder={language === 'fr' ? 'Votre nom' : 'Your last name'}
                      className="border-steel-grey/30"
                    />
                  </div>
                </div>
                
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-abyssal-blue font-medium">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="votre.email@exemple.com"
                    className="border-steel-grey/30"
                  />
                </div>
                
                {/* Company/Entity */}
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-abyssal-blue font-medium">
                    {language === 'fr' ? 'Société' : 'Company'} *
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    placeholder={language === 'fr' ? 'Nom de votre société' : 'Your company name'}
                    className="border-steel-grey/30"
                  />
                </div>

                {/* CV Upload and LinkedIn - Only for "Work With Us" */}
                {formData.type === 'work' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="cvFile" className="text-abyssal-blue font-medium flex items-center gap-2">
                        <Upload size={18} />
                        {language === 'fr' ? 'CV / Curriculum Vitae' : 'CV / Resume'}
                      </Label>
                      <Input
                        id="cvFile"
                        name="cvFile"
                        type="file"
                        onChange={handleChange}
                        accept=".pdf,.doc,.docx"
                        className="border-steel-grey/30 cursor-pointer"
                      />
                      <p className="text-sm text-steel-grey">
                        {language === 'fr' 
                          ? 'Format accepté: PDF, DOC, DOCX (Max 5MB)'
                          : 'Accepted formats: PDF, DOC, DOCX (Max 5MB)'}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="linkedin" className="text-abyssal-blue font-medium">
                        {language === 'fr' ? 'Profil LinkedIn' : 'LinkedIn Profile'}
                      </Label>
                      <Input
                        id="linkedin"
                        name="linkedin"
                        value={formData.linkedin || ''}
                        onChange={handleChange}
                        placeholder="https://linkedin.com/in/votre-profil"
                        className="border-steel-grey/30"
                      />
                    </div>
                  </>
                )}
                
                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-abyssal-blue font-medium">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="resize-none border-steel-grey/30"
                    placeholder={language === 'fr' ? 'Votre message...' : 'Your message...'}
                  />
                </div>

                {/* Hidden field for type */}
                <input type="hidden" name="type" value={formData.type} />
                
                {/* Submit Button - Now using abyssal-blue instead of green */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full text-white group hover:shadow-lg transition-all bg-abyssal-blue hover:bg-abyssal-blue/90"
                >
                  {isSubmitting ? (
                    language === 'fr' ? 'Envoi...' : 'Sending...'
                  ) : (
                    <>
                      {language === 'fr' ? 'Envoyer' : 'Submit'}
                      <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-all" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
